import { inject, Injectable, InjectionToken } from '@angular/core';
import { Analytics } from '@luggagehero/shared/services/analytics';
import { SharedStorageService } from '@luggagehero/shared/services/storage';
import { SharedTranslateLoader, SharedTranslateService } from '@luggagehero/shared/services/translation';
import { SharedWindowService } from '@luggagehero/shared/services/window';
import { Store } from '@ngrx/store';
import { Subscription } from 'rxjs';

import { ILang } from '../../core';
import { IAppState } from '../../ngrx/state/app.state';
import { ChangeAction } from '../actions';
import { CATEGORY } from '../common/category.common';
import { IMultilingualState, initialState } from '../states';

// provide supported languages at runtime
export const Languages: InjectionToken<Array<ILang>> = new InjectionToken('Languages');
export const LanguageProviders = [{ provide: Languages, useValue: [] }];

@Injectable()
export class MultilingualService extends Analytics {
  private translate = inject(SharedTranslateService);
  private window = inject(SharedWindowService);
  private storage = inject(SharedStorageService);
  private store = inject<Store<IAppState>>(Store);

  private requestedLang: string;
  private currentlySettingLanguage: string | null = null;
  private currentLanguageSubscription: Subscription | null = null;
  private initializationComplete = false;
  private readyToSetLanguage = false;
  private pendingLanguage: string | null = null;

  constructor() {
    super();

    this.category = CATEGORY;

    // This language will be used as a fallback when a translation isn't found in the current language
    this.translate.setDefaultLang(initialState.lang);

    // Subscribe to store changes - but don't process until init() has determined the correct language
    this.store
      .select((s) => s.i18n)
      .subscribe((state: IMultilingualState) => {
        if (this.readyToSetLanguage) {
          this.setLang(state.lang);
        } else {
          // Store the pending language to be processed after init()
          this.pendingLanguage = state.lang;
        }
      });
  }

  public async init(params?: Record<string, string>): Promise<void> {
    // Get the language code from the URL or local storage, or use the browser's language as a fallback
    const language = params?.lang || this.storage.language || this.window.locale.split('-')[0];

    // Determine the target language
    const targetLang =
      language && language.length === 2 && language !== initialState.lang ? language : initialState.lang;
    this.requestedLang = targetLang;

    // Now we're ready to set the language - enable the store subscription to work
    this.readyToSetLanguage = true;

    // Set the language directly (this ensures it happens before any pending store updates)
    this.setLang(targetLang);

    // Update the store to keep it in sync (if different from initial state)
    if (targetLang !== initialState.lang) {
      this.store.dispatch(new ChangeAction(targetLang));

      // Wait for the language to be loaded
      try {
        await new Promise<void>((resolve) => {
          const subscription = SharedTranslateLoader.loadedLanguage$.subscribe((loadedLanguages: string[]) => {
            if (loadedLanguages.includes(targetLang)) {
              subscription.unsubscribe();
              resolve();
            }
          });
        });
      } catch {
        // Silently handle any errors - don't break the app
      }
    }

    // Mark initialization as complete - after this point, user can change to any language
    this.initializationComplete = true;
  }

  private setLang(lang: string) {
    // Avoid setting the same language multiple times or overriding a language change in progress
    if (this.currentlySettingLanguage === lang) {
      return;
    }

    // Cancel any ongoing language change subscription
    if (this.currentLanguageSubscription) {
      this.currentLanguageSubscription.unsubscribe();
      this.currentLanguageSubscription = null;
    }

    this.currentlySettingLanguage = lang;

    // Save to local storage:
    // - Always store if user explicitly changed language (after initialization)
    // - During initialization, only store if not the default language (to avoid storing fallback)
    if (this.initializationComplete || lang !== initialState.lang) {
      this.storage.language = lang;
    }

    // Change the language and store the subscription so we can cancel it if needed
    this.currentLanguageSubscription = this.translate.setLanguage(lang).subscribe({
      next: () => {
        // Only clear if we're still setting this language (no newer call interrupted)
        if (this.currentlySettingLanguage === lang) {
          this.currentlySettingLanguage = null;
          if (this.currentLanguageSubscription) {
            this.currentLanguageSubscription.unsubscribe();
            this.currentLanguageSubscription = null;
          }
        }
      },
      error: () => {
        // Clear on error as well
        if (this.currentlySettingLanguage === lang) {
          this.currentlySettingLanguage = null;
          if (this.currentLanguageSubscription) {
            this.currentLanguageSubscription.unsubscribe();
            this.currentLanguageSubscription = null;
          }
        }
      },
    });
  }
}
